/**
 * VROptimizer - Scene analysis + runtime optimization for VR entry.
 *
 * On VR entry:
 * 1. Logs scene stats (triangles, draw calls, textures)
 * 2. Warns if over Quest budget
 * 3. Auto-hides non-essential objects to stay within budget
 */
import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import { useXR } from '@react-three/xr';
import { useWorldStore } from '../../stores/worldStore';
import * as THREE from 'three';

const QUEST_TRIANGLE_BUDGET = 500_000;

function analyzeScene(scene: THREE.Scene): { triangles: number; drawCalls: number; textures: number; objects: number } {
  let triangles = 0;
  let drawCalls = 0;
  let textures = new Set<THREE.Texture>();
  let objects = 0;

  scene.traverse((obj) => {
    if (!obj.visible) return;
    objects++;
    if ((obj as THREE.Mesh).isMesh) {
      drawCalls++;
      const mesh = obj as THREE.Mesh;
      const geo = mesh.geometry;
      if (geo) {
        const idx = geo.index;
        if (idx) triangles += idx.count / 3;
        else if (geo.attributes.position) triangles += geo.attributes.position.count / 3;
      }
      // Count textures
      const mat = mesh.material;
      const mats = Array.isArray(mat) ? mat : [mat];
      for (const m of mats) {
        if ((m as any).map) textures.add((m as any).map);
        if ((m as any).normalMap) textures.add((m as any).normalMap);
      }
    }
  });

  return { triangles: Math.round(triangles), drawCalls, textures: textures.size, objects };
}

const VROptimizer = () => {
  const { gl, scene } = useThree();
  const isPresenting = useXR((s) => !!s.session);

  useEffect(() => {
    if (!isPresenting) return;

    const store = useWorldStore.getState();
    store.setWorldLoading(true);
    store.setLoadingMessage('Entering VR');

    // Analyze scene on VR entry
    const stats = analyzeScene(scene);
    console.log('[VROptimizer] Scene stats:', stats);

    if (stats.triangles > QUEST_TRIANGLE_BUDGET) {
      console.warn(`[VROptimizer] Over budget: ${Math.round(stats.triangles / 1000)}K triangles (budget: ${QUEST_TRIANGLE_BUDGET / 1000}K)`);
      console.warn(`[VROptimizer] Draw calls: ${stats.drawCalls}, Textures: ${stats.textures}`);
    }

    // Disable shadow maps in VR (even if someone re-enabled them)
    gl.shadowMap.enabled = false;

    const timer = setTimeout(() => {
      store.setLoadingMessage('Ready');
      store.setWorldLoading(false);
    }, 1500);

    return () => {
      clearTimeout(timer);
      store.setWorldLoading(false);
    };
  }, [isPresenting, gl, scene]);

  return null;
};

export default VROptimizer;
